/**
 * useSatelliteFrames.ts
 * يقرأ فهرس إطارات القمر الصناعي (satellite_meta.json) الذي يولّده الـbackend.
 *
 * يُستهلَك في مكانين: NewWeatherMap يبني منه **محور الشريط الزمني** حين تكون طبقة القمر
 * فعّالة، و SatelliteTileLayer يختار منه النسيج المعروض. لذا يعيش هنا لا داخل الطبقة —
 * جلبه مرّتين يعني محورين قد يختلفان.
 */

import { useEffect, useState } from 'react';

export interface SatelliteFrame {
    /** مجلّد هرم البلاطات لهذا الإطار، نسبةً إلى rasters/ (مثل "sat/1754400000"). */
    dir: string;
    epoch: number;
}

export interface SatelliteMeta {
    generated_epoch: number;
    /** حجم البلاطة ومدى الـzoom الذي بُني به الهرم — تبنيها الواجهة في مصدر raster. */
    tileSize?: number;
    minzoom?: number;
    maxzoom?: number;
    /** النسيج العالمي الواحد داخل كل مجلّد إطار — المسار السلس (المزج على الـGPU). */
    globalName?: string;
    globalWidth?: number;
    /** حدّا العرض اللذان تمتدّ بينهما بيانات الموزاييك — للتوثيق فقط: التلاشي القطبي
     *  مخبوز في قناة alpha للبلاطات، فلا تحتاجهما الواجهة في قناع. */
    lat_north?: number;
    lat_south?: number;
    frames: SatelliteFrame[];
}

/** دورة تحديث الفهرس — الـbackend يعيد بناء السلسلة كل ~15 دقيقة عبر cron. */
const REFRESH_MS = 15 * 60 * 1000;

export function useSatelliteFrames(enabled: boolean): SatelliteMeta | null {
    const [meta, setMeta] = useState<SatelliteMeta | null>(null);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        const load = () => {
            // ?v يتغيّر كل دورة تحديث: بدونه يخدم الكاش فهرساً قديماً فلا تظهر أحدث صورة.
            fetch(`${import.meta.env.BASE_URL}rasters/satellite_meta.json?v=${Math.floor(Date.now() / REFRESH_MS)}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((m: SatelliteMeta | null) => {
                    if (cancelled) return;
                    // نقبل الإطارات التي تحمل مجلّد بلاطات فقط. الفهرس المولَّد بالمعمارية
                    // السابقة (صورة عالمية واحدة لكل إطار) يحمل `file` لا `dir`، ولولا هذا
                    // الفرز لبنت الواجهة روابط `rasters/undefined/{z}/{x}/{y}.webp` فأمطرت
                    // الخادم بمئات الـ404 لكل زائر حتى تُعاد التوليدة. الفهرس القديم يعني
                    // «لا سلسلة» — فتبقى الطبقة فارغة ويبقى الشريط على محور التوقّعات.
                    const frames = m?.frames?.filter((fr) => !!fr?.dir) ?? [];
                    if (frames.length) setMeta({ ...(m as SatelliteMeta), frames });
                })
                .catch(() => { /* السلسلة غير مولَّدة بعد — تبقى الطبقة فارغة */ });
        };
        load();
        const id = window.setInterval(load, REFRESH_MS);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [enabled]);

    return meta;
}
